import { Container, Graphics, Text } from 'pixi.js';
import { Easings, TweenGroup } from '../../core/Tween';
import type { PickEmPrize } from '../../model/pickem/PickEmTypes';

const FONT_FAMILY = 'system-ui, -apple-system, sans-serif';

export interface PickEmBoxOptions {
  size: number;
  /** Index in the parent grid — passed back through {@code onTap}. */
  index: number;
  /** Pinkish brand accent for the closed-box border + inner glyph. */
  accentColor?: number;
  /** Reveal-state fill (per-box; falls back to prize.tint or warm gold). */
  defaultRevealColor?: number;
  onTap: (index: number) => void;
}

/**
 * One Pick-Em box. Closed state shows a "?" glyph; tapping flips the box
 * to reveal a prize value with a quick scale-x squash → swap → scale-x
 * unsquash, mirroring the gamble-card flip pattern in {@link ../slot/GambleUI}.
 */
export class PickEmBox extends Container {
  private readonly bg: Graphics;
  private readonly face: Container;
  private readonly closedFace: Container;
  private readonly revealedFace: Container;
  private readonly revealedText: Text;
  private readonly tweens = new TweenGroup();
  private revealed = false;

  constructor(public readonly options: PickEmBoxOptions) {
    super();
    this.eventMode = 'static';
    this.cursor = 'pointer';

    const accent = options.accentColor ?? 0xff6bd6;

    this.bg = new Graphics();
    this.bg
      .roundRect(-options.size / 2, -options.size / 2, options.size, options.size, options.size * 0.14)
      .fill({ color: 0x1a0830, alpha: 0.95 })
      .stroke({ color: accent, alpha: 0.85, width: 2 });
    this.addChild(this.bg);

    this.face = new Container();
    this.addChild(this.face);

    this.closedFace = this.buildClosedFace(options.size, accent);
    this.face.addChild(this.closedFace);

    this.revealedFace = new Container();
    this.revealedText = new Text({
      text: '',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: Math.floor(options.size * 0.22),
        fontWeight: '900',
        fill: 0xffffff,
        align: 'center',
      },
    });
    this.revealedText.anchor.set(0.5);
    this.revealedFace.addChild(this.revealedText);
    this.revealedFace.visible = false;
    this.face.addChild(this.revealedFace);

    this.on('pointertap', () => {
      if (this.revealed) return;
      options.onTap(options.index);
    });
  }

  /** Animate the flip and show the prize. Resolves after the flip
   * completes so the host can chain post-reveal sequences. */
  reveal(prize: PickEmPrize): Promise<void> {
    if (this.revealed) return Promise.resolve();
    this.revealed = true;
    this.eventMode = 'auto';
    this.cursor = 'default';

    let swapped = false;
    return this.tweens.startAsync({
      durationMs: 420,
      easing: Easings.easeInOutCubic,
      onUpdate: (p) => {
        const halfP = p < 0.5 ? p * 2 : (1 - p) * 2;
        this.face.scale.x = Math.max(0.05, halfP);
        if (p >= 0.5 && !swapped) {
          swapped = true;
          this.paintRevealed(prize);
        }
      },
      onComplete: () => {
        this.face.scale.x = 1;
      },
    });
  }

  destroyBox(): void {
    this.tweens.cancelAll();
  }

  private buildClosedFace(size: number, accent: number): Container {
    const c = new Container();
    const glyph = new Text({
      text: '?',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: Math.floor(size * 0.55),
        fontWeight: '900',
        fill: accent,
      },
    });
    glyph.anchor.set(0.5);
    c.addChild(glyph);
    return c;
  }

  private paintRevealed(prize: PickEmPrize): void {
    this.closedFace.visible = false;
    this.revealedFace.visible = true;
    const tint = prize.tint ?? this.options.defaultRevealColor ?? 0xfff59d;
    this.revealedText.text = prize.label ?? `+${prize.value}`;
    // Re-tint the box background to celebrate the reveal.
    this.bg.clear();
    const size = this.options.size;
    this.bg
      .roundRect(-size / 2, -size / 2, size, size, size * 0.14)
      .fill({ color: tint, alpha: 0.95 })
      .stroke({ color: 0xffffff, alpha: 0.55, width: 2 });
  }
}
